import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface PageMeta {
  title: string;
  description: string;
  keywords: string;
}

const PAGE_META: Record<string, PageMeta> = {
  '/': {
    title: 'Hogcloud | Websites, Mobile Apps, AI, Cloud, and 3D Printing',
    description:
      'Hogcloud is an established local Northwest Arkansas technology company building affordable websites, mobile apps, AI chatbots, cloud solutions, and custom 3D prints.',
    keywords:
      'Hogcloud, affordable web development Northwest Arkansas, local web developer Fayetteville AR, well rated web development Northwest Arkansas, established website development company, affordable 3D printing Northwest Arkansas, local 3D printing Fayetteville AR, well rated 3D printing Northwest Arkansas, established 3D printing service, custom mobile apps, cloud consulting, AI chatbots, SMS scheduling',
  },
  '/services': {
    title: 'Hogcloud Services | Websites, Apps, Cloud Consulting, AI, 3D Printing',
    description:
      'Explore affordable local Hogcloud services for websites, e-commerce, customer logins, mobile apps, SMS messaging, cloud consulting, AI automation, and custom 3D printing.',
    keywords:
      'affordable local technology services Northwest Arkansas, established web development company, well rated technology services, website services, static website, e-commerce integration, customer login, app development, SMS messaging, scheduling software, cloud consulting, AI automation, custom 3D printing',
  },
  '/services/websites': {
    title: 'Website Services | Static Sites, E-Commerce, SMS, SEO, Chatbots',
    description:
      'Hogcloud creates affordable local websites, SEO-friendly business sites, customer logins, e-commerce integrations, SMS messaging, scheduling, and AI chatbots.',
    keywords:
      'affordable web development Northwest Arkansas, local web developer Fayetteville AR, well rated web development Northwest Arkansas, established website development company, static website, SEO website, web design Fayetteville AR, business website, customer login website, e-commerce integration, payment provider integration, SMS messaging, scheduling, friendly chatbot, AI chatbot',
  },
  '/services/websites/intelligent-chatbots': {
    title: 'AI Integrated Intelligent Chatbots | Web and SMS Support',
    description:
      'Hogcloud builds AI integrated intelligent chatbots and SMS support systems that answer FAQs, reduce repetitive support work, and route complex customer questions.',
    keywords:
      'AI chatbot service Northwest Arkansas, intelligent chatbot, website chatbot, SMS chatbot, FAQ automation, customer support automation, AI customer support, Twilio chatbot, AWS chatbot, Hogcloud AI services',
  },
  '/services/appdev': {
    title: 'Mobile App Development | Customer Logins, SMS, Scheduling, Ordering',
    description:
      'Affordable local custom mobile app development from simple business apps to customer logins, SMS messaging, scheduling, ordering, service requests, and cloud-powered growth.',
    keywords:
      'affordable mobile app development Northwest Arkansas, local app developer Fayetteville AR, established mobile app development company, custom mobile app development, small business app, customer login app, SMS messaging app, scheduling app, ordering app, service request app, cloud mobile app',
  },
  '/services/consulting': {
    title: 'Tech Consulting | Cloud, AI Automation, SMS, Email, Business IT',
    description:
      'Hogcloud offers local tech consulting, AI automation training, AWS architecture guidance, email, SMS, invoicing, and practical business IT support.',
    keywords:
      'local tech consulting Northwest Arkansas, affordable cloud consulting, established IT consulting company, well rated technology consultant, tech consulting, cloud consulting, AWS cloud architecture, AI automation training, agentic AI, business automation, SMS services, email services, invoicing software, IT consulting',
  },
  '/services/3dprints': {
    title: 'Custom 3D Printing | Magnets, Lithophanes, Keychains, Party Favors',
    description:
      'Affordable local custom 3D printing for business magnets, license plates, lithophanes, keychains, character creatures, fan pendants, party favors, badges, and fidgets.',
    keywords:
      'affordable 3D printing Northwest Arkansas, local 3D printing Fayetteville AR, well rated 3D printing Northwest Arkansas, established 3D printing service, custom 3D printing, business magnets, 3D printed license plates, lithophane, custom keychains, fan pendants, party favors, name badges, card trays, fidgets',
  },
  '/pricing': {
    title: 'Hogcloud Pricing | Cloud, Software, AI, Electronics, and Tutoring Rates',
    description:
      'Review Hogcloud hourly rates for cloud support, software development, cloud architecture, AI consulting, electronics, tutoring, after-hours support, and monthly retainers.',
    keywords:
      'Hogcloud pricing, technology consulting rates, cloud support rates, software development rates, AI consulting rates, cloud architecture rates, electronics consulting, tutoring rates, Northwest Arkansas technology consultant',
  },
  '/contact': {
    title: 'Contact Hogcloud | Websites, Apps, Consulting, and 3D Printing',
    description:
      'Contact Hogcloud for affordable local websites, mobile apps, SMS scheduling tools, cloud consulting, AI automation, and custom 3D printing projects.',
    keywords:
      'contact Hogcloud, affordable web design quote, local web developer quote, mobile app quote, affordable 3D printing quote, local 3D printing Fayetteville AR, cloud consulting, AI automation, Fayetteville Arkansas',
  },
  '/shop': {
    title: 'Custom 3D Printed Products | Hogcloud',
    description:
      'Shop affordable local Hogcloud custom 3D printed gifts, magnets, sports pendants, keychains, decorations, party favors, and personalized product ideas.',
    keywords:
      'affordable local 3D printed gifts, shop custom 3D prints, 3D printed gifts, custom magnets, sports pendants, keychains, party favors, personalized 3D printing',
  },
  '/shop/snapcharms': {
    title: 'SnapCharms | Custom 3D Printed Character Charms',
    description:
      'Shop Hogcloud SnapCharms, cute custom 3D printed character charms for keychains, backpacks, necklaces, and pendants.',
    keywords:
      'SnapCharms, 3D printed charms, character keychains, backpack charms, custom pendants, Hogcloud',
  },
  '/shop/sports': {
    title: 'Custom Sports 3D Prints | Jerseys, Pendants, Keychains, Magnets',
    description:
      'Customize sports-themed 3D printed jerseys, pendants, keychains, magnets, emblems, footballs, basketball designs, and fan gifts.',
    keywords:
      'custom sports keychains, 3D printed sports pendants, jersey magnets, football keychains, basketball jersey pendant, fan gifts',
  },
  '/shop/locals': {
    title: 'Local 3D Printed Gifts | Street Signs and City Limits Magnets',
    description:
      'Shop affordable local 3D printed street sign magnets, city limits signs, Fayetteville Arkansas gifts, and personalized local keepsakes.',
    keywords:
      'affordable local 3D printed gifts, local 3D printing Fayetteville AR, street sign magnets, city limits magnets, Fayetteville Arkansas gifts, custom local keepsakes',
  },
  '/about': {
    title: 'About Hogcloud | Creative Technology and Custom 3D Printing',
    description:
      'Learn about Hogcloud, an established local Northwest Arkansas business offering affordable websites, apps, consulting, and custom 3D printing.',
    keywords:
      'about Hogcloud, established local business Northwest Arkansas, affordable technology services, Northwest Arkansas technology business, creative technology, custom websites, app development, 3D printing',
  },
};

const setMetaContent = (selector: string, content: string) => {
  const element = document.head.querySelector<HTMLMetaElement>(selector);
  if (element) {
    element.content = content;
  }
};

const SEO = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const pageMeta = PAGE_META[pathname] || PAGE_META['/'];
    const canonicalUrl = `https://hogcloud.net${pathname === '/' ? '/' : pathname}`;

    document.title = pageMeta.title;
    setMetaContent('meta[name="description"]', pageMeta.description);
    setMetaContent('meta[name="keywords"]', pageMeta.keywords);
    setMetaContent('meta[property="og:title"]', pageMeta.title);
    setMetaContent('meta[property="og:description"]', pageMeta.description);
    setMetaContent('meta[property="og:url"]', canonicalUrl);
    setMetaContent('meta[name="twitter:title"]', pageMeta.title);
    setMetaContent('meta[name="twitter:description"]', pageMeta.description);

    const canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (canonical) {
      canonical.href = canonicalUrl;
    }
  }, [pathname]);

  return null;
};

export default SEO;
